import {
  NetworkStatus,
  useQuery,
  type ApolloError,
  type QueryResult,
} from "@apollo/client";
import { GET_FINDING_COUNTS } from "../FindingsTable/FindingsListApi";

interface FindingCountsData {
  findings?: {
    total_count?: number;
    total_raw_count?: number;
  };
}

interface FindingCountsQueryVars {
  filters_config: {
    filtersjson?: string | null;
    scopesjson?: string | null;
    scopesid?: string | number | null;
    filters_fields_to_override?: string[] | Record<string, unknown>;
  };
}

export interface UseFindingCountsVariables {
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  scopeId?: string | number | null;
  filterFieldsToOverride?: string[] | Record<string, unknown>;
}

export interface UseFindingCountsOptions {
  // Off by default: the Findings page keeps the count in place during a
  // post-mutation refetch (no flicker). The dashboard KPI opts in so `loading`
  // reflects an in-flight refetch and it can show its skeleton.
  notifyOnNetworkStatusChange?: boolean;
}

export interface UseFindingCountsReturnValue {
  totalCount?: number;
  totalRawCount?: number;
  loading: boolean;
  error?: ApolloError;
  refetch: QueryResult<FindingCountsData, FindingCountsQueryVars>["refetch"];
}

const useFindingCounts = (
  variables: UseFindingCountsVariables,
  skip = false,
  options: UseFindingCountsOptions = {}
): UseFindingCountsReturnValue => {
  const { notifyOnNetworkStatusChange = false } = options;

  const { data, previousData, loading, error, refetch, networkStatus } =
    useQuery<FindingCountsData, FindingCountsQueryVars>(GET_FINDING_COUNTS, {
      variables: {
        filters_config: {
          filtersjson: variables.filterToExecute ?? null,
          scopesjson: variables.scopeToExecute || null,
          scopesid: variables.scopeId ?? null,
          ...(variables.filterFieldsToOverride
            ? { filters_fields_to_override: variables.filterFieldsToOverride }
            : {}),
        },
      },
      skip,
      notifyOnNetworkStatusChange,
    });

  const resolvedData = skip ? (data ?? previousData) : data;

  return {
    totalCount: resolvedData?.findings?.total_count,
    totalRawCount: resolvedData?.findings?.total_raw_count,
    loading: loading || networkStatus === NetworkStatus.refetch,
    error,
    refetch,
  };
};

export default useFindingCounts;
